import React, { useState, useEffect } from 'react';
import Toggle from './ui/Toggle';
import { toastSuccess } from '../utils/toast';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import notificationManager from '../utils/notifications';
import NotificationTester from './NotificationTester';
import { getApiUrl } from '../utils/apiUtils';
import { debugLog, debugWarn, debugError } from '../utils/debug';

const NotificationSettings = ({ onNavigateBack }) => {
  const { user } = useUser();
  const [preferences, setPreferences] = useState({
    notifications_enabled: true
  });
  const [messagingSettings, setMessagingSettings] = useState({
    enabled: true,
    daily_message_hour: 10,
    daily_message_minute: 0,
    daily_run_day_frequency: 'always',
    daily_rest_day_frequency: 'sometimes',
    weekly_review_day: 6,
    weekly_review_hour: 17,
    weekly_review_minute: 0,
    weekly_review_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState('default');
  const [pushSubscriptionStatus, setPushSubscriptionStatus] = useState('unknown'); // 'subscribed', 'not-subscribed', 'unknown'
  const [testingWeeklyReview, setTestingWeeklyReview] = useState(false);

  // Days of week for weekly review
  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  // Hours for daily and weekly messages
  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`
  }));

  // Minutes for daily and weekly messages
  const minutes = Array.from({ length: 60 }, (_, i) => ({
    value: i,
    label: i.toString().padStart(2, '0')
  }));

  // Function to get token (same pattern as other components)
  const getToken = async () => {
    try {
      // Get a proper JWT token if possible
      if (user && user.getToken) {
        // Try to get a JWT token from Clerk
        const token = await user.getToken();
        return token;
      } 
      // Use the user ID as a fallback for development/testing
      if (user && user.id) {
        return user.id;
      }
      throw new Error('No authentication token available');
    } catch (error) {
      debugError('Error getting token:', error);
      throw new Error('Authentication error: ' + error.message);
    }
  };

  // Function to get VAPID public key from backend
  const getVapidPublicKey = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/api/vapid-public-key`);
      return response.data.publicKey;
    } catch (error) {
      debugError('Error getting VAPID public key:', error);
      throw error;
    }
  };

  // Function to subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Push messaging is not supported in this browser');
      }

      // Get service worker registration
      debugLog('🔔 Waiting for service worker registration...');
      const registration = await navigator.serviceWorker.ready;
      debugLog('🔔 Service worker ready');
      
      // Check if we already have a subscription
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        debugLog('🔔 Found existing push subscription, verifying with backend...');
        
        // Verify it's still valid on the backend
        try {
          const token = await getToken();
          const response = await axios.get(
            `${getApiUrl()}/api/user/push-subscription`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.hasSubscription) {
            debugLog('✅ Existing push subscription verified');
            setPushSubscriptionStatus('subscribed');
            return true;
          } else {
            debugLog('🔔 Backend has no subscription record, will create new one');
          }
        } catch (error) {
          debugWarn('Error verifying existing subscription:', error);
          // Continue to create a new subscription
        }
      }
      
      // Get VAPID public key from backend
      debugLog('🔔 Getting VAPID public key...');
      const vapidPublicKey = await getVapidPublicKey();
      debugLog('🔔 VAPID public key received');
      
      // Convert VAPID key to Uint8Array
      const vapidKeyUint8 = new Uint8Array(
        atob(vapidPublicKey.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map(char => char.charCodeAt(0))
      );

      // Subscribe to push
      debugLog('🔔 Creating push subscription...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKeyUint8
      });
      debugLog('🔔 Push subscription created:', subscription.endpoint);

      // Send subscription to backend
      debugLog('🔔 Saving subscription to backend...');
      const token = await getToken();
      await axios.post(
        `${getApiUrl()}/api/user/push-subscription`,
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      debugLog('🔔 Subscription saved to backend');

      setPushSubscriptionStatus('subscribed');
      debugLog('✅ Push subscription successful');
      return true;

    } catch (error) {
      debugError('❌ Push subscription failed:', error);
      setPushSubscriptionStatus('not-subscribed');
      
      // Log more specific error details
      if (error.name === 'NotSupportedError') {
        debugError('Push messaging is not supported');
      } else if (error.name === 'NotAllowedError') {
        debugError('Permission for push messaging was not granted');
      } else if (error.name === 'InvalidStateError') {
        debugError('Service worker is in an invalid state');
      } else {
        debugError('Unexpected error:', error.message);
      }
      
      return false;
    }
  };

  // Function to unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove subscription from backend
      const token = await getToken();
      await axios.delete(
        `${getApiUrl()}/api/user/push-subscription`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPushSubscriptionStatus('not-subscribed');
      debugLog('✅ Push unsubscription successful');
      return true;

    } catch (error) {
      debugError('❌ Push unsubscription failed:', error);
      return false;
    }
  };

  // Check push subscription status
  const checkPushSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setPushSubscriptionStatus('not-supported');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Verify with backend
        try {
          const token = await getToken();
          const response = await axios.get(
            `${getApiUrl()}/api/user/push-subscription`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setPushSubscriptionStatus(response.data.hasSubscription ? 'subscribed' : 'not-subscribed');
        } catch (error) {
          debugError('Error checking backend subscription status:', error);
          setPushSubscriptionStatus('not-subscribed');
        }
      } else {
        setPushSubscriptionStatus('not-subscribed');
      }
    } catch (error) {
      debugError('Error checking push subscription:', error);
      setPushSubscriptionStatus('not-subscribed');
    }
  };

  // Check browser notification status and push subscription
  useEffect(() => {
    const checkNotificationStatus = () => {
      const status = notificationManager.getStatus();
      if (status.permission === 'granted') {
        setNotificationStatus('granted');
      } else if (status.permission === 'denied') {
        setNotificationStatus('denied');
      } else {
        setNotificationStatus('default');
      }
    };

    checkNotificationStatus();
    checkPushSubscription(); // Also check push subscription status
    
    // Check periodically in case permission changes
    const interval = setInterval(() => {
      checkNotificationStatus();
      checkPushSubscription();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load user preferences on component mount
  useEffect(() => {
    if (user) {
      loadPreferences();
      loadMessagingSettings();
    }
  }, [user]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const response = await axios.get(
        `${getApiUrl()}/api/user/notification-preferences`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setPreferences(response.data);
    } catch (err) {
      debugError('Error loading notification preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const loadMessagingSettings = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${getApiUrl()}/messages/user-messaging-settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessagingSettings({
        enabled: response.data.enabled || true,
        daily_message_hour: response.data.daily_message_hour || 10,
        daily_message_minute: response.data.daily_message_minute || 0,
        daily_run_day_frequency: response.data.daily_run_day_frequency || 'always',
        daily_rest_day_frequency: response.data.daily_rest_day_frequency || 'sometimes',
        weekly_review_day: response.data.weekly_review_day || 6,
        weekly_review_hour: response.data.weekly_review_hour || 17,
        weekly_review_minute: response.data.weekly_review_minute || 0,
        weekly_review_enabled: response.data.weekly_review_enabled !== false
      });
    } catch (err) {
      debugError('Error loading messaging settings:', err);
      // Use defaults if loading fails
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      setError(null);

      const token = await getToken();

      // Save notification preferences
      const notificationResponse = await axios.put(
        `${getApiUrl()}/api/user/notification-preferences`,
        preferences,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Save messaging settings
      const messagingResponse = await axios.put(
        `${getApiUrl()}/messages/user-messaging-settings`,
        messagingSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setPreferences(notificationResponse.data);

      // Confirm save with toast, then navigate back
      toastSuccess('Notification preferences saved.');
      setTimeout(() => {
        if (onNavigateBack) onNavigateBack();
      }, 700);
      
    } catch (err) {
      debugError('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleNotifications = async (enabled) => {
    // If enabling notifications, request browser permission and set up push
    if (enabled) {
      debugLog('🔔 User enabled notifications, checking permission and push subscription');
      
      try {
        // Always ensure we have browser permission first
        let permission = notificationStatus;
        if (permission !== 'granted') {
          debugLog('🔔 Requesting browser permission...');
          permission = await notificationManager.requestPermission();
          debugLog('🔔 Permission result:', permission);
        }
        
        if (permission === 'granted') {
          setNotificationStatus('granted');
          
          // Always try to set up push subscription when enabling notifications
          // This ensures we create a subscription even if browser permission was already granted
          debugLog('🔔 Setting up push subscription...');
          const pushSuccess = await subscribeToPush();
          if (!pushSuccess) {
            debugWarn('Push subscription failed, but browser notifications are enabled');
            setError('Failed to set up push notifications. Please try again or check your browser settings.');
            return;
          }
          
          // Test notification to confirm it works
          setTimeout(() => {
            notificationManager.showNotification('🎉 Notifications Enabled!', {
              body: 'You\'ll now receive run reminders and updates from your coach',
              tag: 'welcome-notification'
            });
          }, 500);
        } else {
          // Permission denied - don't enable notifications
          setNotificationStatus('denied');
          setError('Browser notifications were denied. Please enable them in your browser settings to receive notifications.');
          return;
        }
      } catch (error) {
        debugError('Error setting up notifications:', error);
        setError('Failed to set up notifications: ' + error.message);
        return;
      }
    } else if (!enabled) {
      // If disabling notifications, unsubscribe from push
      await unsubscribeFromPush();
    }

    setPreferences(prev => ({
      ...prev,
      notifications_enabled: enabled
    }));
  };

  const testWeeklyReview = async () => {
    try {
      setTestingWeeklyReview(true);
      setError(null);

      const token = await getToken();
      const response = await axios.post(
        `${getApiUrl()}/messages/test-weekly-review`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.status === 'success') {
        toastSuccess('Test notification sent.');
      } else {
        setError(response.data.message || 'Failed to send test weekly review');
      }
    } catch (err) {
      debugError('Error testing weekly review:', err);
      setError(err.response?.data?.detail || 'Failed to send test weekly review');
    } finally {
      setTestingWeeklyReview(false);
    }
  };

  // Don't render anything if user is not loaded
  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="relative mb-4">
              <div className="w-8 h-8 border-2 border-blue-400/30 rounded-full mx-auto"></div>
              <div className="absolute top-0 left-0 w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-400">Loading notification settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900 relative">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto p-6 pb-20">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Notification Settings</h1>
            <p className="text-gray-400">
              Manage your notification and messaging preferences
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Browser permission status warning */}
          {preferences.notifications_enabled && notificationStatus === 'denied' && (
            <div className="mb-6 p-4 bg-orange-900/20 border border-orange-500/50 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-orange-300 mb-3">
                    Browser notifications are blocked. To receive notifications, please enable them in your browser settings.
                  </p>
                  <button 
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                    onClick={async () => {
                      try {
                        const permission = await notificationManager.requestPermission();
                        if (permission === 'granted') {
                          setNotificationStatus('granted');
                          setError(null);
                          // Test notification
                          setTimeout(() => {
                            notificationManager.showNotification('🎉 Notifications Enabled!', {
                              body: 'You\'ll now receive run reminders and updates from your coach',
                              tag: 'welcome-notification'
                            });
                          }, 500);
                        }
                      } catch (error) {
                        debugError('Error requesting notification permission:', error);
                      }
                    }}
                  >
                    Enable Browser Notifications
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Notifications Toggle */}
          <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">Push Notifications</h3>
                <p className="text-gray-400 text-sm">
                  Enable or disable all push notifications from Runium. This controls notifications from your coach and run reminders.
                </p>
              </div>
              <div className="ml-6">
                <Toggle checked={preferences.notifications_enabled} onChange={(v) => handleToggleNotifications(v)} />
              </div>
            </div>
          </div>

          {/* Push Subscription Status Warning */}
          {preferences.notifications_enabled && pushSubscriptionStatus === 'not-subscribed' && notificationStatus === 'granted' && (
            <div className="mb-6 p-4 bg-orange-900/20 border border-orange-500/50 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-orange-300 mb-3">
                    Push notifications are enabled, but the push subscription is not set up. You may not receive notifications.
                  </p>
                  <button 
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                    onClick={async () => {
                      debugLog('🔔 Manually setting up push subscription...');
                      setError(null);
                      const success = await subscribeToPush();
                      if (success) {
                        debugLog('✅ Push subscription set up successfully');
                        // Test notification
                        setTimeout(() => {
                          notificationManager.showNotification('🎉 Push Notifications Fixed!', {
                            body: 'You should now receive notifications properly',
                            tag: 'fix-notification'
                          });
                        }, 500);
                      } else {
                        setError('Failed to set up push subscription. Please check your browser settings and try again.');
                      }
                    }}
                  >
                    Fix Push Notifications
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Daily Check-in Messages */}
          <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white">Daily Check-in Messages</h3>
                <Toggle
                  checked={messagingSettings.enabled}
                  onChange={(v) => setMessagingSettings(prev => ({ ...prev, enabled: v }))}
                  ariaLabel="Daily check-in messages"
                />
              </div>
              <p className="text-gray-400 text-sm">
                Receive daily messages from your coach to check in on your running progress.
              </p>
            </div>

            <div className={`space-y-4 ${!messagingSettings.enabled ? 'opacity-50' : ''}`}>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Time of day
                </label>
                <div className="flex gap-2">
                  <select
                    value={messagingSettings.daily_message_hour}
                    onChange={(e) => setMessagingSettings(prev => ({ ...prev, daily_message_hour: parseInt(e.target.value) }))}
                    disabled={!messagingSettings.enabled}
                    className={`flex-1 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      !messagingSettings.enabled ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    {hours.map(hour => (
                      <option key={hour.value} value={hour.value}>{hour.label}</option>
                    ))}
                  </select>
                  <span className="text-white self-center">:</span>
                  <select
                    value={messagingSettings.daily_message_minute}
                    onChange={(e) => setMessagingSettings(prev => ({ ...prev, daily_message_minute: parseInt(e.target.value) }))}
                    disabled={!messagingSettings.enabled}
                    className={`w-20 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      !messagingSettings.enabled ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    {minutes.map(minute => (
                      <option key={minute.value} value={minute.value}>{minute.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    On days with runs
                  </label>
                  <select
                    value={messagingSettings.daily_run_day_frequency}
                    onChange={(e) => setMessagingSettings(prev => ({ ...prev, daily_run_day_frequency: e.target.value }))}
                    disabled={!messagingSettings.enabled}
                    className={`w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      !messagingSettings.enabled ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="always">Always</option>
                    <option value="sometimes">Sometimes (50%)</option>
                    <option value="never">Never</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    On rest days
                  </label>
                  <select
                    value={messagingSettings.daily_rest_day_frequency}
                    onChange={(e) => setMessagingSettings(prev => ({ ...prev, daily_rest_day_frequency: e.target.value }))}
                    disabled={!messagingSettings.enabled}
                    className={`w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      !messagingSettings.enabled ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="always">Always</option>
                    <option value="sometimes">Sometimes (50%)</option>
                    <option value="never">Never</option>
                  </select>
                </div>
              </div>

              {messagingSettings.enabled && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-300">
                        You'll receive check-in messages at{' '}
                        <span className="font-semibold">
                          {hours.find(h => h.value === messagingSettings.daily_message_hour)?.label}:{messagingSettings.daily_message_minute.toString().padStart(2, '0')}
                        </span>{' '}
                        daily. Run days: {messagingSettings.daily_run_day_frequency}, 
                        Rest days: {messagingSettings.daily_rest_day_frequency}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Weekly Review Messages */}
          <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white">Weekly Review Messages</h3>
                <Toggle
                  checked={messagingSettings.weekly_review_enabled}
                  onChange={(v) => setMessagingSettings(prev => ({ ...prev, weekly_review_enabled: v }))}
                  ariaLabel="Weekly review messages"
                />
              </div>
              <p className="text-gray-400 text-sm">
                Get a weekly summary of your progress and plan for the upcoming week.
              </p>
            </div>

            <div className={`space-y-4 ${!messagingSettings.weekly_review_enabled ? 'opacity-50' : ''}`}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Day of week
                  </label>
                  <select
                    value={messagingSettings.weekly_review_day}
                    onChange={(e) => setMessagingSettings(prev => ({ ...prev, weekly_review_day: parseInt(e.target.value) }))}
                    disabled={!messagingSettings.weekly_review_enabled}
                    className={`w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      !messagingSettings.weekly_review_enabled ? 'cursor-not-allowed' : ''
                    }`}
                  >
                    {daysOfWeek.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Time of day
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={messagingSettings.weekly_review_hour}
                      onChange={(e) => setMessagingSettings(prev => ({ ...prev, weekly_review_hour: parseInt(e.target.value) }))}
                      disabled={!messagingSettings.weekly_review_enabled}
                      className={`flex-1 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        !messagingSettings.weekly_review_enabled ? 'cursor-not-allowed' : ''
                      }`}
                    >
                      {hours.map(hour => (
                        <option key={hour.value} value={hour.value}>{hour.label}</option>
                      ))}
                    </select>
                    <span className="text-white self-center">:</span>
                    <select
                      value={messagingSettings.weekly_review_minute}
                      onChange={(e) => setMessagingSettings(prev => ({ ...prev, weekly_review_minute: parseInt(e.target.value) }))}
                      disabled={!messagingSettings.weekly_review_enabled}
                      className={`w-20 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        !messagingSettings.weekly_review_enabled ? 'cursor-not-allowed' : ''
                      }`}
                    >
                      {minutes.map(minute => (
                        <option key={minute.value} value={minute.value}>{minute.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {messagingSettings.weekly_review_enabled && (
                <>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-300">
                          You'll receive a weekly review every{' '}
                          <span className="font-semibold">
                            {daysOfWeek.find(d => d.value === messagingSettings.weekly_review_day)?.label}
                          </span>{' '}
                          at{' '}
                          <span className="font-semibold">
                            {hours.find(h => h.value === messagingSettings.weekly_review_hour)?.label}:{messagingSettings.weekly_review_minute.toString().padStart(2, '0')}
                          </span>{' '}
                          with your progress summary and next week's plan.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Test Weekly Review Button */}
                  <div className="mt-4">
                    <button
                      onClick={testWeeklyReview}
                      disabled={testingWeeklyReview}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        testingWeeklyReview
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900'
                      }`}
                    >
                      {testingWeeklyReview ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 border-2 border-gray-400/30 rounded-full mr-2 animate-spin border-t-gray-400"></div>
                          Sending Test Review...
                        </div>
                      ) : (
                        'Test Weekly Review Now'
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Send a test weekly review message to see how it looks
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={savePreferences}
              disabled={saving}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                saving
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900'
              }`}
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-gray-400/30 rounded-full mr-2"></div>
                  <div className="absolute top-0 left-0 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </div>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>

          {/* Notification Tester - Development Only */}
          {process.env.NODE_ENV === 'development' && <NotificationTester />}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;